// src/components/shared/OfflineBanner.js
// Unobtrusive connectivity indicator for the offline-capable PWA.
//   - Offline: persistent pill "Offline — showing saved data".
//   - Online:  a brief "Synced Xm ago" pill after each background sync pass.
import React, { useEffect, useState } from 'react';
import styled from 'styled-components';
import { FaWifi, FaCloudDownloadAlt } from 'react-icons/fa';
import useOnlineStatus from '../../offline/useOnlineStatus';
import { getLastSyncedAt } from '../../offline/offlineDb';

const Pill = styled.div`
  position: fixed;
  bottom: ${p => p.theme.spacing(2)};
  left: 50%;
  transform: translateX(-50%);
  z-index: 4000;
  display: inline-flex;
  align-items: center;
  gap: 8px;
  padding: 8px 16px;
  border-radius: 20px;
  font-size: 0.8rem;
  font-family: ${p => p.theme.typography.fontFamily};
  color: ${p => p.theme.colors.textPrimary};
  background: ${p => (p.$offline ? p.theme.colors.primary : p.theme.colors.surface)};
  border: 1px solid ${p => (p.$offline ? p.theme.colors.primary : p.theme.colors.inputGray + '55')};
  box-shadow: 0 4px 16px rgba(0, 0, 0, 0.35);
  opacity: ${p => (p.$visible ? 1 : 0)};
  pointer-events: ${p => (p.$visible ? 'auto' : 'none')};
  transition: opacity 0.3s ease;

  svg { flex-shrink: 0; }

  @media (max-width: 768px) {
    bottom: ${p => p.theme.spacing(9)}; /* clear the mobile bottom nav */
  }
`;

const relativeTime = (ts) => {
  if (!ts) return null;
  const secs = Math.max(0, Math.round((Date.now() - ts) / 1000));
  if (secs < 60) return 'just now';
  const mins = Math.round(secs / 60);
  if (mins < 60) return `${mins}m ago`;
  const hrs = Math.round(mins / 60);
  if (hrs < 24) return `${hrs}h ago`;
  return `${Math.round(hrs / 24)}d ago`;
};

export default function OfflineBanner() {
  const online = useOnlineStatus();
  const [lastSync, setLastSync] = useState(getLastSyncedAt());
  const [showSynced, setShowSynced] = useState(false);

  // Reveal the "synced" pill briefly whenever a background sync finishes.
  useEffect(() => {
    const onComplete = () => {
      setLastSync(getLastSyncedAt());
      setShowSynced(true);
      const t = setTimeout(() => setShowSynced(false), 4000);
      return () => clearTimeout(t);
    };
    window.addEventListener('offline-sync-complete', onComplete);
    return () => window.removeEventListener('offline-sync-complete', onComplete);
  }, []);

  if (!online) {
    return (
      <Pill $offline $visible role="status" aria-live="polite">
        <FaWifi />
        Offline — showing saved data
      </Pill>
    );
  }

  const rel = relativeTime(lastSync);
  return (
    <Pill $offline={false} $visible={showSynced && !!rel} role="status" aria-live="polite">
      <FaCloudDownloadAlt />
      {rel ? `Synced ${rel}` : 'Synced'}
    </Pill>
  );
}
